/**
 * workoutsStore — ONE shared listWorkouts result for the entire app.
 *
 * Previously SetTab and WorkoutList each called bridge.listWorkouts() on
 * mount — two identical network calls producing two identical arrays. This
 * module lifts the single call to a shared writable store. Any component
 * that needs the workout list reads from here instead of fetching.
 *
 * Usage:
 *   import { workouts, workoutsLoaded, loadWorkouts } from "../lib/workoutsStore";
 *   onMount(() => loadWorkouts());  // idempotent: second call is a no-op if already loaded
 */
import { writable } from "svelte/store";
import type { WorkoutSummary } from "@contract/types";
import { bridge, BridgeUnavailableError } from "../bridge/client";
import { offline } from "./state";
import { STUB_WORKOUTS } from "./stub";
import { setSupportedMethods, setNativeWebBuild } from "./nativeMethods";

export const workouts = writable<WorkoutSummary[]>([]);
export const workoutsLoaded = writable<boolean>(false);

let _fetchStarted = false;

/** Load workouts from the bridge into the shared store. Idempotent: subsequent
 *  calls while a fetch is in-flight or after a successful load are no-ops. */
export async function loadWorkouts(): Promise<void> {
  if (_fetchStarted) return;
  _fetchStarted = true;

  try {
    if (!bridge.isAvailable()) {
      offline.set("no-handler");
      workouts.set(STUB_WORKOUTS);
      return;
    }
    const hs = await bridge.handshake();
    // Capture the native method registry so method-gated affordances (e.g. the
    // open-water route card) can hide themselves on shells that lack the method.
    setSupportedMethods(hs?.supportedMethods);
    // Capture which web build the SHELL says it painted (compiled-in vs downloaded, and
    // whether it is the build the site last served). The page cannot tell — both are
    // file:// loads — and the Save-health line must not guess. See buildSourceLabel().
    setNativeWebBuild(hs);
    const result = await bridge.listWorkouts({
      activityType: "swimming",
      limit: 100,
    });
    workouts.set(result);
    offline.set("ok");
  } catch (err) {
    // Health declined is an answer, not an outage: show the swimmer's real (empty)
    // list, never the demo swims (Beta App Review walk, 2026-09-13). Measured after
    // "Don't Allow": native sends HEALTHKIT_QUERY_FAILED "Authorization not determined";
    // HEALTHKIT_AUTH_DENIED is the code native should send once it maps that error.
    const healthDenied =
      err instanceof Error &&
      (err.message.startsWith("HEALTHKIT_AUTH_DENIED") ||
        (err.message.startsWith("HEALTHKIT_QUERY_FAILED") && err.message.includes("Authorization not determined")));
    if (healthDenied) {
      offline.set("health-off");
    } else if (err instanceof BridgeUnavailableError) {
      offline.set(err.reason);
    } else {
      offline.set("rejected");
    }
    workouts.set(healthDenied ? [] : STUB_WORKOUTS);
  } finally {
    workoutsLoaded.set(true);
  }
}

/** Re-run the workout-list fetch, bypassing the one-shot `_fetchStarted` guard.
 *  Called on the native `swimset:auth-resolved` nudge (G2): the list that came
 *  back empty during the first-launch HealthKit auth race can now populate.
 *  Idempotent + harmless if the list already loaded — it re-reads the same data,
 *  and keeps the current array on screen until the new fetch resolves. */
export async function refetchWorkouts(): Promise<void> {
  _fetchStarted = false;
  await loadWorkouts();
}

/** Reset for testing only — allows tests to re-trigger the fetch path. */
export function _resetForTests(): void {
  _fetchStarted = false;
  workouts.set([]);
  workoutsLoaded.set(false);
}
